import { NavLink, useLocation } from 'react-router-dom';
import {
  HiOutlineViewGrid,
  HiOutlineLogout,
  HiOutlinePhone,
  HiOutlineClipboardList,
  HiOutlineBookOpen,
  HiOutlineAdjustments,
  HiX
} from 'react-icons/hi';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { useEffect } from 'react';

const navItems = [
  { path: '/', icon: HiOutlineViewGrid, label: 'Dashboard' },
  { path: '/catalogues', icon: HiOutlineBookOpen, label: 'Catalogues' },
  { path: '/sizes', icon: HiOutlineAdjustments, label: 'Sizes' },
  { path: '/contacts', icon: HiOutlinePhone, label: 'Contacts' },
  { path: '/enquiries', icon: HiOutlineClipboardList, label: 'Enquiries' },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { isOpen, close } = useSidebar();
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    close();
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const desktopSidebarContent = (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-dark-400">
        <div className="flex items-center gap-3">
          <img src="/logo.svg" alt="KG Luxury" className="w-10 h-10" />
          <div>
            <h1 className="font-display text-xl font-bold gold-gradient-text">KG Luxury</h1>
            <p className="text-[10px] text-gray-500 uppercase tracking-widest">Admin Panel</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 pt-6 overflow-y-auto">
        <p className="text-[10px] text-gray-500 uppercase tracking-widest px-4 mb-3">Menu</p>
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-gold-500/20 to-gold-600/10 text-gold-500 border-l-2 border-gold-500'
                      : 'text-gray-400 hover:bg-dark-500 hover:text-gray-200'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium text-sm">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-dark-400">
        <div className="flex items-center gap-3 px-3 py-3 bg-dark-500 rounded-xl mb-3">
          <div className="w-10 h-10 bg-gradient-to-br from-gold-500 to-gold-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
            <span className="text-dark-900 font-bold">
              {user?.full_name?.charAt(0).toUpperCase() || 'A'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-200 truncate">{user?.full_name || 'Admin'}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="flex items-center justify-center gap-2 px-4 py-2.5 w-full rounded-xl text-gray-400 hover:bg-red-500/10 hover:text-red-400 border border-dark-400 hover:border-red-500/30 transition-all duration-200"
        >
          <HiOutlineLogout className="w-4 h-4" />
          <span className="font-medium text-sm">Sign Out</span>
        </button>
      </div>
    </>
  );

  const mobileSidebarContent = (
    <>
      {/* Logo with close button */}
      <div className="p-6 border-b border-dark-400 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.svg" alt="KG Luxury" className="w-10 h-10" />
          <div>
            <h1 className="font-display text-xl font-bold gold-gradient-text">KG Luxury</h1>
            <p className="text-[10px] text-gray-500 uppercase tracking-widest">Admin Panel</p>
          </div>
        </div>
        <button
          onClick={close}
          className="p-2 text-gray-400 hover:text-white"
          aria-label="Close menu"
        >
          <HiX className="w-6 h-6" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 pt-6 overflow-y-auto">
        <p className="text-[10px] text-gray-500 uppercase tracking-widest px-4 mb-3">Menu</p>
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                onClick={close}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-gold-500/20 to-gold-600/10 text-gold-500 border-l-2 border-gold-500'
                      : 'text-gray-400 hover:bg-dark-500 hover:text-gray-200'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium text-sm">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-dark-400">
        <div className="flex items-center gap-3 px-3 py-3 bg-dark-500 rounded-xl mb-3">
          <div className="w-10 h-10 bg-gradient-to-br from-gold-500 to-gold-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
            <span className="text-dark-900 font-bold">
              {user?.full_name?.charAt(0).toUpperCase() || 'A'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-200 truncate">{user?.full_name || 'Admin'}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="flex items-center justify-center gap-2 px-4 py-2.5 w-full rounded-xl text-gray-400 hover:bg-red-500/10 hover:text-red-400 border border-dark-400 hover:border-red-500/30 transition-all duration-200"
        >
          <HiOutlineLogout className="w-4 h-4" />
          <span className="font-medium text-sm">Sign Out</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar - Always visible on lg screens */}
      <aside className="hidden lg:flex w-72 bg-dark-600 border-r border-dark-400 min-h-screen flex-col">
        {desktopSidebarContent}
      </aside>

      {/* Mobile Overlay - Controlled by isOpen state, hidden on lg via CSS */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 lg:hidden transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={close}
        aria-hidden="true"
      />

      {/* Mobile Sidebar - Slide in/out based on isOpen, hidden on lg via CSS */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 bg-dark-600 border-r border-dark-400 z-50 flex flex-col transform transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {mobileSidebarContent}
      </aside>
    </>
  );
}
